import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSet } from 'react-use';
import { useDebounce } from 'use-debounce';
import { useIsEmbeddedMobile } from '../../../../../../../_hooks/useIsEmbeddedMobile';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { RawButton } from '../../../../../../../CommonComponents/OPL/Button/Button.styles';
import { Cell, HtmlTable } from '../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Icon } from '../../../../../../../CommonComponents/OPL/Icon/Icon';
import { Input } from '../../../../../../../CommonComponents/OPL/Input/Input';
import { TableWrapper } from '../../../../../../../CommonComponents/OPL/Table/Table';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { TruncatedText } from '../../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Wrapper } from '../../../../../../../CommonComponents/OPL/Wrapper/Wrapper';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../../../../../../CommonUtils/formatNumber';
import { ActivityCsvExport } from '../../../../../../components/ActivityCsvExport/ActivityCsvExport';
import { PerformanceTableRow } from '../../../../../../components/PerformanceDetails/PerformanceDetails.styles';
import { sortGainLossTableRows } from '../../../../../../helpers/sortTableRows';
import { useExportCsvButtonContainer } from '../../../../../../hooks/useExportCsvButtonContainer';
import {
  Cell as CellProps,
  useRealizedGainLoss,
} from '../../../../../../hooks/useRealizedGainLoss';
import {
  Sorting,
  SortingDirection,
} from '../../../../../Positions/components/AlternativeDetail/AlternativeDetail.meta';
import { useFilteredRows } from './hooks/useFilteredRows';
import { RealizedGainLossDetailsEmpty } from './RealizedGainLossDetails.empty';
import { RealizedGainLossDetailsError } from './RealizedGainLossDetails.error';
import { RealizedGainLossDetailsLoading } from './RealizedGainLossDetails.loading';
import { PositionsDTO, TaxLotDetailsDTO } from '../../../GainLossMobile/GainLossMobile.meta';
import { PositionMobile } from '../../../GainLossMobile/components/PositionMobile';
import { PositionsListMobile } from '../../../GainLossMobile/components/PoistionsListMobile';
import { TaxLotMobile } from '../../../GainLossMobile/components/TaxLotMobile';

const formatValue = (type: string | undefined, value: any) => {
  if (value === null || value === '') return '';
  switch (type) {
    case 'currency':
      return formatCurrency(value, { decimalPlaces: 2 });
    case 'number':
      return formatNumber(value, { decimalPlaces: 2 });
    case 'date':
      return moment(value).format('M/D/YYYY');
    case 'percent':
      return formatPercentage(value / 100, { decimalPlaces: 2 });
    default:
      return value;
  }
};

export const TableCell = ({ type, text: value, align = 'right', fontWeight = 600 }: CellProps) => {
  return (
    <Text
      fontWeight={fontWeight}
      textAlign={align}
      variant="tabular"
      width="100%"
      color={
        type === 'currency' && typeof value === 'number' && value < 0 ? 'error.500' : 'neutral.700'
      }
    >
      {formatValue(type, value)}
    </Text>
  );
};

interface Props {
  title: string;
}

export const RealizedGainLossDetails = ({ title }: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sorting, setSorting] = useState<Sorting>({});
  const [positionMobile, setPositionMobile] = useState<PositionsDTO | null>(null);
  const [taxLotsMobile, setTaxLotsMobile] = useState<TaxLotDetailsDTO[] | null>(null);

  const isEmbeddedMobile = useIsEmbeddedMobile();

  const [, { has: isRowExpanded, toggle: toggleRow }] = useSet<string>();

  const [, { has: isChildRowExpanded, toggle: toggleChildRow }] = useSet<string>();

  const [debouncedSearchTerm] = useDebounce(searchTerm, 200);

  const { data, isFetching, isError, refetch } = useRealizedGainLoss();

  useEffect(() => {
    setPositionMobile(null);
    setTaxLotsMobile(null);
  }, [isFetching]);

  const unfilteredRows = useMemo(() => {
    if (!data?.tableData?.rows) {
      return null;
    }

    if (Object.keys(sorting).length === 0) {
      return data.tableData.rows;
    }

    const sortingKey = Object.keys(sorting)[0];

    return sortGainLossTableRows(data.tableData.rows, sortingKey, sorting[sortingKey]);
  }, [data, sorting]);

  const rows = useFilteredRows({ rows: unfilteredRows, filter: debouncedSearchTerm });

  const exportCsvButtonContainer = useExportCsvButtonContainer();

  if (isFetching) {
    return <RealizedGainLossDetailsLoading title={title} />;
  }

  if (isError) {
    return <RealizedGainLossDetailsError title={title} onRetry={refetch} />;
  }

  if (!data) {
    return null;
  }

  const { tableData, gainLoss } = data;

  if (!tableData) {
    return null;
  }

  if (tableData.rows.length === 0) {
    return <RealizedGainLossDetailsEmpty title={title} />;
  }

  return (
    <>
      {!!exportCsvButtonContainer &&
        createPortal(<ActivityCsvExport tab="rgl" />, exportCsvButtonContainer)}

      {taxLotsMobile ? (
        <TaxLotMobile
          type="realized"
          taxLots={taxLotsMobile}
          goBack={() => setTaxLotsMobile(null)}
        />
      ) : positionMobile ? (
        <PositionMobile
          type="realized"
          position={positionMobile}
          goBack={() => setPositionMobile(null)}
          setTaxSlots={setTaxLotsMobile}
        />
      ) : (
        <Wrapper>
          <TableWrapper mb="4">
            <Box display="flex" flexDirection="column" p="12">
              {!isEmbeddedMobile && (
                <Text variant="cardTitle" mb="12">
                  {title}
                </Text>
              )}
              <Input
                value={searchTerm}
                onChange={({ target: { value } }) => setSearchTerm(value)}
                placeholder="Search..."
                leftIcon="fa fa-search"
                aria-label="Search"
              />
            </Box>

            <HtmlTable
              variant="embedded"
              columnSpacing="medium"
              maxHeight={isEmbeddedMobile ? 'auto' : 600}
              fixedHead
            >
              <table>
                <colgroup>
                  <col width="10%" />
                  <col width="0%" />
                </colgroup>

                <thead>
                  <tr>
                    {tableData.headers.map(({ text, isMobile = false }, index) => (
                      <th
                        key={text}
                        style={
                          index === 0
                            ? {
                                minWidth: isEmbeddedMobile ? 'auto' : '250px',
                                display: !isEmbeddedMobile || isMobile ? 'table-cell' : 'none',
                              }
                            : {
                                display: !isEmbeddedMobile || isMobile ? 'table-cell' : 'none',
                              }
                        }
                      >
                        <RawButton
                          onClick={() => {
                            if (sorting[index]) {
                              setSorting(
                                sorting[index] === SortingDirection.Asc
                                  ? { [index]: SortingDirection.Desc }
                                  : {},
                              );
                            } else {
                              setSorting({ [index]: SortingDirection.Asc });
                            }
                          }}
                          aria-label="Sort by column"
                          css="width: 100%"
                        >
                          <Box display="flex" alignItems="center" css="gap: 4px" p="2">
                            <Text
                              variant="tableHeader"
                              textAlign={index === 0 ? 'left' : 'right'}
                              width="100%"
                            >
                              {text}
                            </Text>
                            {(sorting[index] === SortingDirection.Asc ||
                              sorting[index] === SortingDirection.Desc) && (
                              <Icon
                                className={
                                  sorting[index] === SortingDirection.Asc
                                    ? 'fas fa-arrow-up'
                                    : 'fas fa-arrow-down'
                                }
                                color="neutral.500"
                              />
                            )}
                          </Box>
                        </RawButton>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {(rows ?? []).map(({ id, cells, childRows }, ind) => {
                    const hasChildRows = childRows && childRows.length > 0;
                    const isExpanded = isRowExpanded(id) || (!!searchTerm && hasChildRows);
                    const tableRowVariant = hasChildRows ? 'clickable' : 'default';

                    return (
                      <React.Fragment key={id}>
                        <PerformanceTableRow
                          isExpanded={isExpanded}
                          onClick={hasChildRows && !searchTerm ? () => toggleRow(id) : undefined}
                          variant={tableRowVariant}
                        >
                          {cells.map(({ text, type, align, isMobile = false }, index) => (
                            <>
                              {(!isEmbeddedMobile || isMobile) && (
                                <Cell key={index} isFlex={index === 0}>
                                  <Box display="flex" alignItems="center">
                                    {index === 0 && hasChildRows && !searchTerm && (
                                      <Icon
                                        className={
                                          isExpanded
                                            ? 'fas fa-chevron-down'
                                            : 'fas fa-chevron-right'
                                        }
                                        color="neutral.400"
                                        mr={isExpanded ? 8 : 12}
                                      />
                                    )}

                                    {index === 0 ? (
                                      <>
                                        <TruncatedText
                                          fontWeight={600}
                                          color="neutral.700"
                                          textAlign="left"
                                          minWidth={150}
                                        >
                                          {text}
                                        </TruncatedText>
                                      </>
                                    ) : (
                                      <TableCell text={text} type={type} align={align} />
                                    )}
                                  </Box>
                                </Cell>
                              )}
                            </>
                          ))}
                        </PerformanceTableRow>

                        {isExpanded && childRows && (
                          <>
                            {isEmbeddedMobile ? (
                              <PositionsListMobile
                                positions={gainLoss[ind].positions}
                                setPositionMobile={setPositionMobile}
                              />
                            ) : (
                              childRows.map(({ id, cells, lotDetailsRows }) => {
                                const hasLotDetails = lotDetailsRows && lotDetailsRows.length > 0;
                                const isChildExpanded =
                                  isChildRowExpanded(id) || (!!searchTerm && hasLotDetails);
                                const tableRowVariant = hasLotDetails ? 'clickable' : 'default';

                                return (
                                  <React.Fragment key={id}>
                                    <PerformanceTableRow
                                      isExpanded={isChildExpanded}
                                      onClick={
                                        hasLotDetails && !searchTerm
                                          ? () => toggleChildRow(id)
                                          : undefined
                                      }
                                      variant={tableRowVariant}
                                    >
                                      {cells.map((childCell, index) => (
                                        <Cell key={index} isFlex={index === 0}>
                                          <Box display="flex" alignItems="center">
                                            {index === 0 && hasChildRows && !searchTerm && (
                                              <Icon
                                                className={
                                                  isChildExpanded
                                                    ? 'fas fa-chevron-down'
                                                    : 'fas fa-chevron-right'
                                                }
                                                color="neutral.400"
                                                mr={isChildExpanded ? 8 : 12}
                                                ml={8}
                                              />
                                            )}

                                            {index === 0 ? (
                                              <>
                                                <TruncatedText
                                                  fontWeight={600}
                                                  color="neutral.700"
                                                  textAlign="left"
                                                  minWidth={150}
                                                >
                                                  {childCell.text}
                                                </TruncatedText>
                                              </>
                                            ) : (
                                              <TableCell
                                                text={childCell.text}
                                                type={childCell.type}
                                                align={childCell.align}
                                              />
                                            )}
                                          </Box>
                                        </Cell>
                                      ))}
                                    </PerformanceTableRow>
                                    {isChildExpanded &&
                                      lotDetailsRows &&
                                      lotDetailsRows.map(({ id, cells }) => {
                                        return (
                                          <React.Fragment key={id}>
                                            <PerformanceTableRow>
                                              {cells.map((lotDetailsCell, index) => (
                                                <Cell key={index} isFlex={index === 0}>
                                                  <Box display="flex" alignItems="center">
                                                    <TableCell
                                                      text={lotDetailsCell.text}
                                                      type={lotDetailsCell.type}
                                                      align={lotDetailsCell.align}
                                                      fontWeight={400}
                                                    />
                                                  </Box>
                                                </Cell>
                                              ))}
                                            </PerformanceTableRow>
                                          </React.Fragment>
                                        );
                                      })}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </HtmlTable>
          </TableWrapper>
        </Wrapper>
      )}
    </>
  );
};
